import { CssBaseline } from "@mui/material";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { deDE, enUS, fiFI, frFR, nbNO } from "@mui/x-date-pickers/locales";
import deLocale from "date-fns/locale/de";
import enLocale from "date-fns/locale/en-GB";
import fiLocale from "date-fns/locale/fi";
import frLocale from "date-fns/locale/fr";
import nbLocale from "date-fns/locale/nb";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { selectUxFlavor } from "slices/explorer";
import { UxFlavor } from "slices/explorer/types";

import { procoreTheme, theme } from "../app/theme";

const localeMap: Record<string, { mui: typeof enUS; dateFns: Locale }> = {
    en: { mui: enUS, dateFns: enLocale },
    de: { mui: deDE, dateFns: deLocale },
    fr: { mui: frFR, dateFns: frLocale },
    no: { mui: nbNO, dateFns: nbLocale },
    fi: { mui: fiFI, dateFns: fiLocale },
};

export function GeneralProvider({ children, uxFlavor: forceUxFlavor }: { children: ReactNode; uxFlavor?: UxFlavor }) {
    const { i18n } = useTranslation();
    const [locale, setLocale] = useState(localeMap[i18n.language as keyof typeof localeMap] || localeMap.en);
    const storeUxFlavor = useAppSelector(selectUxFlavor);

    // forceUxFlavor is needed because storeUxFlavor might be not initialized with a proper value
    // at the moment theme is already needed (e.g. app loading screen)
    // So for lib mode we just force it to Procore since the very beginning
    const uxFlavor = forceUxFlavor || storeUxFlavor;

    useEffect(() => {
        setLocale(localeMap[i18n.language] || localeMap.en);
    }, [i18n.language]);
    return (
        <>
            <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={locale.dateFns}
                localeText={locale.mui.components.MuiLocalizationProvider.defaultProps.localeText}
            >
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={uxFlavor === UxFlavor.Procore ? procoreTheme : theme}>
                        <CssBaseline />
                        {children}
                    </ThemeProvider>
                </StyledEngineProvider>
            </LocalizationProvider>
        </>
    );
}
